import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { settingsRepository } from '@/data/settings/repositories/settings_repository_impl'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { KeysFormErrors } from '../models/settings_ui_state'

export function useKeysSettingsViewModel() {
  const navigate = useNavigate()
  const [fcmKey, setFcmKey] = useState('')
  const [twilioAccountSid, setTwilioAccountSid] = useState('')
  const [twilioAuthToken, setTwilioAuthToken] = useState('')
  const [errors, setErrors] = useState<KeysFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const data = await settingsRepository.getKeys()
        if (!cancelled) {
          setFcmKey(data.fcmKey)
          setTwilioAccountSid(data.twilioAccountSid)
          setTwilioAuthToken(data.twilioAuthToken)
        }
      } catch (error) {
        logError('Failed to load keys:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitting(true)

      const newErrors: KeysFormErrors = {}
      if (!fcmKey.trim()) newErrors.fcmKey = 'FCM Key is required'
      if (!twilioAccountSid.trim()) newErrors.twilioAccountSid = 'Twilio Account SID is required'
      if (!twilioAuthToken.trim()) newErrors.twilioAuthToken = 'Twilio Auth Token is required'

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        setIsSubmitting(false)
        return
      }

      try {
        await settingsRepository.saveKeys({
          fcmKey: fcmKey.trim(),
          twilioAccountSid: twilioAccountSid.trim(),
          twilioAuthToken: twilioAuthToken.trim(),
        })
        alert('Keys saved successfully!')
      } catch (error: unknown) {
        logError('Failed to save keys:', error)
        alert(getAxiosLikeErrorMessage(error, 'Failed to save keys. Please try again.'))
      } finally {
        setIsSubmitting(false)
      }
    },
    [fcmKey, twilioAccountSid, twilioAuthToken]
  )

  const handleCancel = useCallback(() => navigate('/admin'), [navigate])

  return {
    fcmKey,
    twilioAccountSid,
    twilioAuthToken,
    errors,
    isSubmitting,
    isLoading,
    setFcmKey,
    setTwilioAccountSid,
    setTwilioAuthToken,
    setErrors,
    handleSubmit,
    handleCancel,
  }
}
